import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  Switch,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Slider,
  IconButton,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  Button,
} from '@mui/material';
import {
  ArrowBack,
  Notifications,
  Language,
  Brightness4,
  Lock,
  TextFields,
  RestartAlt,
  Logout,
  Check,
  Close,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { logout } from '../../store/slices/authSlice';
import { axelionColors } from '../../theme/axelionTheme';

const SettingsPageGlass = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Default settings
  const defaultSettings = {
    emailNotifications: true,
    pushNotifications: true,
    language: 'ru',
    darkMode: false,
    profileVisibility: 'public',
    dataSharing: false,
    showEmail: true,
    showPhone: false,
    fontSize: 16,
    compactMode: false,
  };

  // Load settings from localStorage or use defaults
  const loadSettings = () => {
    const saved = localStorage.getItem('appSettings');
    if (saved) {
      try {
        return { ...defaultSettings, ...JSON.parse(saved) };
      } catch (e) {
        return defaultSettings;
      }
    }
    return defaultSettings;
  };

  // State management
  const [settings, setSettings] = useState(loadSettings);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);

  // Detect changes
  useEffect(() => {
    const saved = loadSettings();
    const isChanged = JSON.stringify(saved) !== JSON.stringify(settings);
    setHasChanges(isChanged);
  }, [settings]);

  // Handler functions
  const handleToggle = (key, label) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleLanguageChange = (event) => {
    const newLang = event.target.value;
    setSettings((prev) => ({
      ...prev,
      language: newLang,
    }));
  };

  const handleVisibilityChange = (event) => {
    const newVisibility = event.target.value;
    setSettings((prev) => ({
      ...prev,
      profileVisibility: newVisibility,
    }));
  };

  const handleFontSizeChange = (event, newValue) => {
    setSettings((prev) => ({
      ...prev,
      fontSize: newValue,
    }));
  };

  const handleSaveSettings = () => {
    localStorage.setItem('appSettings', JSON.stringify(settings));
    setHasChanges(false);
    toast.success('Настройки сохранены успешно', {
      position: 'bottom-center',
      autoClose: 2000,
    });
  };

  const handleResetToDefaults = () => {
    setSettings(defaultSettings);
    toast.success('Настройки сброшены к значениям по умолчанию', {
      position: 'bottom-center',
      autoClose: 2000,
    });
  };

  // Section component for consistent styling
  const SettingSection = ({ icon, title, subtitle, children }) => (
    <Card
      sx={{
        bgcolor: axelionColors.bgLight,
        border: `1px solid ${axelionColors.borderLight}`,
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(26, 26, 26, 0.06)',
        p: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: axelionColors.accentLight,
            border: `1px solid ${axelionColors.borderLight}`,
            borderRadius: '8px',
            color: axelionColors.gold,
            mr: 3,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              color: axelionColors.textDark,
              mb: 0.5,
              letterSpacing: '0.02em',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {children}
    </Card>
  );

  // Toggle item component
  const ToggleItem = ({ label, description, checked, onChange }) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 2,
        '&:not(:last-child)': {
          borderBottom: `1px solid ${axelionColors.borderLight}`,
        },
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500, color: axelionColors.textDark, mb: 0.5 }}>
          {label}
        </Typography>
        {description && (
          <Typography variant="caption" sx={{ color: axelionColors.textMuted }}>
            {description}
          </Typography>
        )}
      </Box>
      <Switch
        checked={checked}
        onChange={onChange}
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: axelionColors.gold,
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: axelionColors.gold,
          },
          '& .MuiSwitch-track': {
            backgroundColor: axelionColors.borderLight,
          },
        }}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: axelionColors.bgCream,
        pb: 6,
      }}
    >

      {/* Header */}
      <Box
        sx={{
          bgcolor: axelionColors.bgLight,
          borderBottom: `1px solid ${axelionColors.borderLight}`,
          pt: 3,
          pb: 3,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: axelionColors.textDark,
                bgcolor: axelionColors.bgLight,
                border: `1px solid ${axelionColors.borderLight}`,
                boxShadow: '0 2px 6px rgba(26, 26, 26, 0.06)',
                '&:hover': {
                  bgcolor: axelionColors.bgCream,
                  borderColor: axelionColors.gold,
                },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 300,
                  color: axelionColors.textDark,
                  letterSpacing: '0.05em',
                }}
              >
                Настройки
              </Typography>
              <Typography variant="body2" sx={{ color: axelionColors.textMuted, mt: 0.5 }}>
                Управление параметрами приложения
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Stack spacing={3}>
          {/* Notifications Section */}
          <SettingSection
            icon={<Notifications sx={{ fontSize: 28 }} />}
            title="Уведомления"
            subtitle="Управление параметрами уведомлений"
          >
            <ToggleItem
              label="Email уведомления"
              description="Получать уведомления на электронную почту"
              checked={settings.emailNotifications}
              onChange={() => handleToggle('emailNotifications')}
            />
            <ToggleItem
              label="Push-уведомления"
              description="Получать push-уведомления в браузере"
              checked={settings.pushNotifications}
              onChange={() => handleToggle('pushNotifications')}
            />
          </SettingSection>

          {/* Privacy Section */}
          <SettingSection
            icon={<Lock sx={{ fontSize: 28 }} />}
            title="Приватность"
            subtitle="Управление видимостью вашего профиля"
          >
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel
                component="legend"
                sx={{
                  color: axelionColors.textDark,
                  fontWeight: 500,
                  mb: 2,
                  fontSize: '0.95rem',
                  '&.Mui-focused': {
                    color: axelionColors.textDark,
                  },
                }}
              >
                Видимость профиля
              </FormLabel>
              <RadioGroup value={settings.profileVisibility} onChange={handleVisibilityChange}>
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    value="public"
                    control={
                      <Radio
                        sx={{
                          color: axelionColors.textMuted,
                          '&.Mui-checked': {
                            color: axelionColors.gold,
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: axelionColors.textDark, mb: 0.25 }}
                        >
                          Публичный
                        </Typography>
                        <Typography variant="caption" sx={{ color: axelionColors.textMuted }}>
                          Ваш профиль виден всем пользователям
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    value="contacts"
                    control={
                      <Radio
                        sx={{
                          color: axelionColors.textMuted,
                          '&.Mui-checked': {
                            color: axelionColors.gold,
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: axelionColors.textDark, mb: 0.25 }}
                        >
                          Только контакты
                        </Typography>
                        <Typography variant="caption" sx={{ color: axelionColors.textMuted }}>
                          Только ваши контакты могут видеть профиль
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    value="private"
                    control={
                      <Radio
                        sx={{
                          color: axelionColors.textMuted,
                          '&.Mui-checked': {
                            color: axelionColors.gold,
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: axelionColors.textDark, mb: 0.25 }}
                        >
                          Приватный
                        </Typography>
                        <Typography variant="caption" sx={{ color: axelionColors.textMuted }}>
                          Ваш профиль скрыт от всех
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </RadioGroup>
            </FormControl>

            <Divider sx={{ my: 3, borderColor: axelionColors.borderLight }} />

            <ToggleItem
              label="Показывать email"
              description="Ваш email будет виден другим пользователям"
              checked={settings.showEmail}
              onChange={() => handleToggle('showEmail')}
            />

            <ToggleItem
              label="Показывать телефон"
              description="Ваш номер телефона будет виден другим пользователям"
              checked={settings.showPhone}
              onChange={() => handleToggle('showPhone')}
            />

            <Divider sx={{ my: 3, borderColor: axelionColors.borderLight }} />

            <ToggleItem
              label="Общий доступ данных"
              description="Разрешить анализ данных для улучшения сервиса"
              checked={settings.dataSharing}
              onChange={() => handleToggle('dataSharing')}
            />
          </SettingSection>

          {/* Language Section */}
          <SettingSection
            icon={<Language sx={{ fontSize: 28 }} />}
            title="Язык"
            subtitle="Выберите язык интерфейса"
          >
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <RadioGroup value={settings.language} onChange={handleLanguageChange}>
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    value="ru"
                    control={
                      <Radio
                        sx={{
                          color: axelionColors.textMuted,
                          '&.Mui-checked': {
                            color: axelionColors.gold,
                          },
                        }}
                      />
                    }
                    label={<Typography sx={{ color: axelionColors.textDark, fontWeight: 500 }}>Русский</Typography>}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    value="uz"
                    control={
                      <Radio
                        sx={{
                          color: axelionColors.textMuted,
                          '&.Mui-checked': {
                            color: axelionColors.gold,
                          },
                        }}
                      />
                    }
                    label={<Typography sx={{ color: axelionColors.textDark, fontWeight: 500 }}>O'zbekcha</Typography>}
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    value="en"
                    control={
                      <Radio
                        sx={{
                          color: axelionColors.textMuted,
                          '&.Mui-checked': {
                            color: axelionColors.gold,
                          },
                        }}
                      />
                    }
                    label={<Typography sx={{ color: axelionColors.textDark, fontWeight: 500 }}>English</Typography>}
                  />
                </Box>
              </RadioGroup>
            </FormControl>
          </SettingSection>

          {/* Theme Section */}
          <SettingSection
            icon={<Brightness4 sx={{ fontSize: 28 }} />}
            title="Тема"
            subtitle="Параметры отображения интерфейса"
          >
            <ToggleItem
              label="Темная тема"
              description="Включить темный режим интерфейса"
              checked={settings.darkMode}
              onChange={() => handleToggle('darkMode')}
            />
            <Box
              sx={{
                mt: 3,
                p: 3,
                bgcolor: settings.darkMode ? axelionColors.textDark : axelionColors.bgWarm,
                border: `1px solid ${axelionColors.borderLight}`,
                borderRadius: '8px',
                transition: 'all 0.3s ease',
              }}
            >
              <Typography variant="body2" sx={{ color: settings.darkMode ? axelionColors.bgLight : axelionColors.textDark, fontWeight: 500 }}>
                Предварительный просмотр темы
              </Typography>
            </Box>
          </SettingSection>

          {/* Display Section */}
          <SettingSection
            icon={<TextFields sx={{ fontSize: 28 }} />}
            title="Отображение"
            subtitle="Настройки размера и расположения элементов"
          >
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, color: axelionColors.textDark }}>
                  Размер шрифта
                </Typography>
                <Box
                  sx={{
                    bgcolor: axelionColors.accentLight,
                    border: `1px solid ${axelionColors.borderLight}`,
                    color: axelionColors.gold,
                    px: 2,
                    py: 0.75,
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  {settings.fontSize}px
                </Box>
              </Box>
              <Slider
                value={settings.fontSize}
                onChange={handleFontSizeChange}
                min={12}
                max={24}
                step={1}
                marks={[
                  { value: 12, label: '12px' },
                  { value: 16, label: '16px' },
                  { value: 20, label: '20px' },
                  { value: 24, label: '24px' },
                ]}
                sx={{
                  color: axelionColors.gold,
                  '& .MuiSlider-thumb': {
                    backgroundColor: axelionColors.gold,
                    boxShadow: '0 2px 6px rgba(184, 149, 110, 0.3)',
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: axelionColors.gold,
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: axelionColors.borderLight,
                  },
                  '& .MuiSlider-mark': {
                    backgroundColor: axelionColors.textMuted,
                  },
                  '& .MuiSlider-markLabel': {
                    color: axelionColors.textMuted,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                  },
                }}
              />
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  bgcolor: axelionColors.bgWarm,
                  border: `1px solid ${axelionColors.borderLight}`,
                  borderRadius: '8px',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: `${settings.fontSize}px`,
                    color: axelionColors.textDark,
                    fontWeight: 500,
                  }}
                >
                  Пример текста с выбранным размером шрифта
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3, borderColor: axelionColors.borderLight }} />

            <ToggleItem
              label="Компактный режим"
              description="Уменьшить отступы для более плотного интерфейса"
              checked={settings.compactMode}
              onChange={() => handleToggle('compactMode')}
            />
          </SettingSection>

          {/* Action Buttons */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
            {/* Save Button */}
            <Card
              sx={{
                bgcolor: axelionColors.bgLight,
                border: `1px solid ${axelionColors.borderLight}`,
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(26, 26, 26, 0.06)',
                p: 3,
              }}
            >
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<Check />}
                onClick={handleSaveSettings}
                disabled={!hasChanges}
                sx={{
                  bgcolor: axelionColors.gold,
                  color: '#FFFFFF',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  py: 1.5,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: axelionColors.goldDark,
                    boxShadow: 'none',
                  },
                  '&:disabled': {
                    bgcolor: axelionColors.bgBeige,
                    color: axelionColors.textMuted,
                  },
                }}
              >
                Сохранить изменения
              </Button>
              {hasChanges && (
                <Typography
                  variant="caption"
                  sx={{
                    color: axelionColors.gold,
                    display: 'block',
                    mt: 2,
                    textAlign: 'center',
                    fontWeight: 500,
                  }}
                >
                  У вас есть несохраненные изменения
                </Typography>
              )}
            </Card>

            {/* Reset Button */}
            <Card
              sx={{
                bgcolor: axelionColors.bgLight,
                border: `1px solid ${axelionColors.borderLight}`,
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(26, 26, 26, 0.06)',
                p: 3,
              }}
            >
              <Button
                variant="outlined"
                fullWidth
                size="large"
                startIcon={<RestartAlt />}
                onClick={handleResetToDefaults}
                sx={{
                  borderColor: axelionColors.borderLight,
                  color: axelionColors.textDark,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  py: 1.5,
                  borderRadius: '8px',
                  '&:hover': {
                    borderColor: axelionColors.gold,
                    bgcolor: axelionColors.bgCream,
                  },
                }}
              >
                Сбросить
              </Button>
            </Card>
          </Box>

          {/* Logout Section */}
          <Card
            sx={{
              bgcolor: axelionColors.bgLight,
              border: `1px solid ${axelionColors.borderLight}`,
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(26, 26, 26, 0.06)',
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Logout sx={{ color: axelionColors.error, mr: 1.5, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: axelionColors.textDark }}>
                    Выйти из аккаунта
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: axelionColors.textMuted, display: 'block' }}>
                  Завершить текущий сеанс и вернуться на страницу входа
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => setConfirmDialog(true)}
                sx={{
                  bgcolor: axelionColors.error,
                  color: '#FFFFFF',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  px: 3,
                  py: 1,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#B91C1C',
                    boxShadow: 'none',
                  },
                }}
              >
                Выйти
              </Button>
            </Box>
          </Card>

          {/* Info Card */}
          <Card
            sx={{
              bgcolor: axelionColors.bgWarm,
              border: `1px solid ${axelionColors.borderLight}`,
              borderRadius: '8px',
              boxShadow: 'none',
              p: 3,
            }}
          >
            <Typography variant="body2" sx={{ color: axelionColors.textMuted, textAlign: 'center' }}>
              Все настройки сохраняются в локальном хранилище браузера
            </Typography>
          </Card>
        </Stack>
      </Container>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        PaperProps={{
          sx: {
            bgcolor: axelionColors.bgLight,
            border: `1px solid ${axelionColors.borderLight}`,
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(26, 26, 26, 0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: axelionColors.textDark,
            fontWeight: 500,
            fontSize: '1.25rem',
            letterSpacing: '0.02em',
          }}
        >
          Подтверждение выхода
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: axelionColors.textMuted, mt: 2 }}>
            Вы уверены, что хотите выйти из аккаунта?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setConfirmDialog(false)}
            startIcon={<Close />}
            sx={{
              borderColor: axelionColors.borderLight,
              color: axelionColors.textDark,
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              px: 2,
              py: 1,
              borderRadius: '8px',
              '&:hover': {
                borderColor: axelionColors.gold,
                bgcolor: axelionColors.bgCream,
              },
            }}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              dispatch(logout());
              toast.success('Вы вышли из аккаунта');
              navigate('/login');
            }}
            startIcon={<Check />}
            sx={{
              bgcolor: axelionColors.error,
              color: '#FFFFFF',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              px: 2,
              py: 1,
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#B91C1C',
                boxShadow: 'none',
              },
            }}
          >
            Выйти
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPageGlass;
